import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { isApiError } from "@majourneys/api-client";
import { activitiesApi } from "../../../features/activities/api/activities.api";
import { activityHrefParts } from "../../../features/activities/lib/activity-href";
import { transferHubRedirect } from "../../../features/transport/lib/hub-redirect";
import { ErrorPage } from "../../../features/errors";

/**
 * Legacy 3-segment redirect: /<lang>/<cat>/<slug>.
 *
 * Catches old indexed URLs like `/en/spas/marajah-spa` or `/fr/Spas/foo`
 * (note the case mismatch) that GSC still surfaces from the previous
 * site structure. Resolves the activity by slug and 301-redirects to the
 * canonical 4-segment URL `/<lang>/<city>/<category-or-segment>/<slug>`.
 *
 * The `$cat` param is intentionally unused for resolution — the slug
 * alone is unique, and the activity's own city/category metadata
 * determines the canonical path. So `/en/spas/marajah-spa`,
 * `/en/wellness/marajah-spa`, and `/en/anything/marajah-spa` all
 * funnel to the same canonical URL.
 *
 * Literal route segments at this depth (about, blog, faq, contact,
 * discover, explore, category, place, …) are matched by their own
 * file-based routes first — TanStack prefers literal over dynamic — so
 * this loader only fires for genuinely unrecognized first segments.
 *
 * Slug not resolvable → `notFound()` so SSR returns a real HTTP 404.
 */
export const Route = createFileRoute("/$lang/_layout/$cat/$slug/")({
  beforeLoad: async ({ params }) => {
    const { lang, slug } = params;
    let activity: Awaited<ReturnType<typeof activitiesApi.getBySlug>>["data"] | null = null;
    try {
      const response = await activitiesApi.getBySlug(slug);
      activity = response.data;
    } catch (error) {
      if (isApiError(error) && error.status === 404) {
        throw notFound();
      }
      throw error;
    }
    if (!activity?.slug) throw notFound();
    // A transfer route has no page: straight to the hub, one hop, not via
    // the canonical URL.
    const hub = transferHubRedirect(activity as Parameters<typeof transferHubRedirect>[0], lang);
    if (hub) throw redirect(hub);
    const aMode = (activity as { mode?: string }).mode ?? "bookable";
    const aSegment = (activity as { segment?: string | null }).segment ?? null;
    const parts = activityHrefParts({
      lang,
      citySlug: activity.city?.slug ?? null,
      categorySlug: activity.category?.slug ?? null,
      segment: aMode === "explore" ? aSegment : null,
      slug: activity.slug,
    });
    throw redirect({
      to: parts.to,
      params: parts.params,
      search: (prev: Record<string, unknown>) => prev,
      replace: true,
      statusCode: 301,
    });
  },
  component: () => <ErrorPage status={404} />,
});
